import { Injectable } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  /**
   * Formats a date as MM/DD/YYYY HH:mm AM/PM
   * @param date - The date to format, or undefined
   * @returns Formatted date string, or empty string if date is undefined
   */
  formatDateTime(date: Date | undefined): string {
    if (!date) return '';

    const dateObj = new Date(date);
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    const year = dateObj.getFullYear();

    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours || 12;
    const formattedHours = hours.toString().padStart(2, '0');

    return `${month}/${day}/${year} ${formattedHours}:${minutes} ${ampm}`;
  }

  /**
   * Checks if a date falls on a weekend (Saturday or Sunday)
   * @param date - The date to check
   * @returns True if the date is a weekend, false otherwise
   */
  isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  /**
   * Adds business days to a date, excluding weekends
   * @param startDate - The starting date
   * @param days - The number of business days to add
   * @returns A new Date object with the business days added
   */
  addBusinessDays(startDate: Date, days: number): Date {
    const result = new Date(startDate);
    result.setHours(0, 0, 0, 0);

    if (days <= 0) {
      return result;
    }

    let remaining = days;
    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      if (!this.isWeekend(result)) {
        remaining -= 1;
      }
    }

    return result;
  }

  /**
   * Subtracts business days from a date, excluding weekends
   * @param startDate - The starting date
   * @param days - The number of business days to subtract
   * @returns A new Date object with the business days subtracted
   */
  subtractBusinessDays(startDate: Date, days: number): Date {
    const result = new Date(startDate);
    result.setHours(0, 0, 0, 0);

    if (days <= 0) {
      return result;
    }

    let remaining = days;
    while (remaining > 0) {
      result.setDate(result.getDate() - 1);
      if (!this.isWeekend(result)) {
        remaining -= 1;
      }
    }

    return result;
  }

  /**
   * Converts a Date or NgbDateStruct to a Date object
   * @param value - The date value to convert (Date or NgbDateStruct)
   * @returns A Date object
   */
  toDate(value: Date | NgbDateStruct): Date {
    if (value instanceof Date) {
      return value;
    }

    return new Date(value.year, value.month - 1, value.day);
  }

  /**
   * Formats a date in short format (e.g., "Jan 15, 2024")
   * @param date - The date to format
   * @returns Formatted date string, or empty string if date is undefined
   */
  formatDateShort(date: Date | undefined | null): string {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /**
   * Adds or subtracts years from a date
   * @param date - The starting date
   * @param years - The number of years to add (positive) or subtract (negative)
   * @returns A new Date object with the years added/subtracted
   */
  addYears(date: Date, years: number): Date {
    const result = new Date(date);
    result.setFullYear(result.getFullYear() + years);
    return result;
  }
}

